import React from 'react';
import classNames from 'classnames';
import { ILocationState, useModule } from 'utils/hooks';
import DMESearchLookUpDrawer from '../common/SearchLookUpDrawer/Containers/DashboardMaintenanceEnquiry';
import { ActionBar, BreadcrumbBar } from '@markinson/uicomponents-v2';
import { Route, withRouter } from 'react-router';
import { withStyles } from '@material-ui/core';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import styles from './DashboardMaintenanceEnquiry.styles';
import { IDashboardMaintenanceEnquiryProps } from './DashboardMaintenanceEnquiry.properties';
import DashboardDetails from './DashboardDetails';
import modules from './DashboardMaintenanceEnquiry.modules';
import { useTabLocations } from './DashboardMaintenanceEnquiry.hooks';
import options from './DashboardMaintenanceEnquiry.options';
import OptionsMenu from 'components/common/OptionsMenu';
import { useDMEContextSelector } from './DashboardMaintenanceEnquiry.context';
import DashboardLayout from './DashboardLayout';
import AddWidgetDialog from './AddWidgetDialog';
import QuickLinks from './QuickLinks';

const DashboardMaintenanceEnquiry = (props: IDashboardMaintenanceEnquiryProps) => {
    const { selectedDashboardId, selectedDashboardType, classes, history, location, match, isDashboardMaintenanceEnquiryLookUpOpen, isMenuOptionOpen, searchById, toggleMenuOption } = props;
    const url = match ? match.url : '';
    const path = match ? match.path : '';
    const { actionBarProps } = React.useContext(ActionBarContext);
    const { tabLocations } = useTabLocations(url, location, selectedDashboardId);
    const params = new URLSearchParams(location.search);
    const dashboardId = useDMEContextSelector<'DashboardId'>((state) => state.DashboardId) || Number(params.get('DashboardId'));
    const [addWidgetDialogOpen, setAddWidgetDialogOpen] = React.useState<boolean>(false);

    const { selectedTab, handleModuleChange } = useModule(
        history,
        location,
        'DashboardMaintenanceEnquiry',
        tabLocations,
        modules,
    );

    React.useEffect(
        () => {
            const urlParams = new URLSearchParams(location.search);
            if (dashboardId && Number(dashboardId) > 1) {
                searchById(urlParams.get('DashboardId'));
            }

            return () => {
                props?.resetSearchLookupDrawer();
                props?.destoryForm();
            };
        },
        []
    );

    const handleToggleMenuOptionOpen = React.useCallback(
        () => {
            toggleMenuOption(!isMenuOptionOpen);
        },
        [isMenuOptionOpen, toggleMenuOption]
    );
    const dashboardDetailsPageRef = React.useRef<any>();

    const handleActionBarButtonClick = React.useCallback(
        (action: string) => {
            switch (action) {
                case 'AddWidget':
                    setAddWidgetDialogOpen(true);
                    break;
                case 'DashboardDetailsOnAdd':
                case 'DashboardDetailsOnEdit':
                case 'DashboardDetailsOnDelete':
                case 'DashboardDetailsCancel':
                case 'DashboardDetailsOk':
                    dashboardDetailsPageRef.current?.[action]();
                    break;
                case 'FieldsBackButtonClick':
                    _handleModuleChange('DashboardDetails');
                    break;
                default:
            }
        },
        [selectedTab, dashboardDetailsPageRef]
    );
    React.useEffect(
        () => {
            if (history.location.pathname === '/enquiry-dashboard-maintenance') {
                const urlParams = new URLSearchParams(location.search);
                const newPath = dashboardId
                    ? `/enquiry-dashboard-maintenance/dashboard-details?DashboardId=${urlParams.get('DashboardId')}`
                    : '/enquiry-dashboard-maintenance/dashboard-details';
                history.push(newPath);
            }
        },
        [history]
    );

    const _handleModuleChange = React.useCallback(
        (tabToNavigate, inlineObject?: ILocationState) => {
            if (!selectedDashboardId) return;

            if (selectedTab === 'DashboardDetails' && !dashboardDetailsPageRef.current?.isChangesSavedOrDiscarded()) {
                return;
            }

            if (selectedTab === 'DashboardDetails' && tabToNavigate === 'DashboardDetails') {
                return;
            }

            handleModuleChange(tabToNavigate, inlineObject ?? { search: (selectedTab === 'DashboardDetails') ? { DashboardId: selectedDashboardId } : null });
        },
        [selectedTab, dashboardDetailsPageRef, selectedDashboardId, handleModuleChange]
    );

    return (
        <div className={classes.mainDiv}>
            <div className={classes.innerContainer}>
                <DMESearchLookUpDrawer enableToggle disabled={false} />
                <div
                    className={classNames(classes.DMESection, {
                        [classes.DMESectionMarginOpen]: isDashboardMaintenanceEnquiryLookUpOpen,
                        [classes.DMESectionMarginClose]: !isDashboardMaintenanceEnquiryLookUpOpen
                    })}
                >
                    <BreadcrumbBar
                        mainModule={'Dashboard Maintenance'}
                        activeModuleId={selectedTab}
                        moduleTree={modules}
                        onBreadcrumbClick={_handleModuleChange}
                        onMainModuleClick={() => _handleModuleChange('DashboardDetails')}
                        onOptionsMenuClick={handleToggleMenuOptionOpen}
                    />
                    <Route
                        key={'DashboardDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}dashboard-details`}
                        exact
                        render={() => <DashboardDetails innerRef={dashboardDetailsPageRef} />}
                    />
                    <Route
                        key={'DashboardLayout'}
                        path={`${path.endsWith('/') ? path : `${path}/`}dashboard-layout`}
                        exact
                        render={() => <DashboardLayout innerRef={dashboardDetailsPageRef} />}
                    />
                    <Route
                        key={'QuickLinks'}
                        path={`${path.endsWith('/') ? path : `${path}/`}quick-links`}
                        exact
                        render={() => <QuickLinks selectedDashboardType={selectedDashboardType} selectedDashboardId={selectedDashboardId} innerRef={dashboardDetailsPageRef} />}
                    />
                </div>
            </div>
            <OptionsMenu
                open={isMenuOptionOpen}
                options={options.DME}
                onOptionClick={_handleModuleChange}
                defaultValue={selectedTab}
            />
            <ActionBar {...actionBarProps} onActionClick={handleActionBarButtonClick} />
            {
                addWidgetDialogOpen ? (
                    <AddWidgetDialog
                        open={addWidgetDialogOpen}
                        initialValues={{}}
                        valuesSchema={{}}
                        onClose={() => setAddWidgetDialogOpen(false)}
                    />
                ) : undefined
            }
        </div >
    );
};

export default withRouter(withStyles(styles, { index: 1 })(DashboardMaintenanceEnquiry));
